import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DraggableSyntheticListeners } from '@dnd-kit/core';
import { IconEdit, IconSortHandle } from '../../../../../assets/icons';
import { PopUpNav } from '../../../../../components';
import { IconShare } from '../../../../../assets/popup-nav-icons';
import { UserGroup, UserPermissionType } from '../../../../../data-access/gql-types/graphql';
import { useUserPermissions } from '../../../../../hooks/user/use-user-permissions';
import { useShareRedirect } from '../../../../../modules/installation-share/hooks/use-share-redirect';
import { ROUTES } from '../../../../../routes/routes';
import './index.scss';

type GroupItemProps = {
  id: string;
  groups: UserGroup[];
  isDraggable?: boolean;
  setNodeRef?: (node: HTMLElement | null) => void;
  style?: React.CSSProperties;
  attributes?: Record<string, unknown>;
  listeners?: DraggableSyntheticListeners;
};

export const GroupItem: React.FC<GroupItemProps> = ({
  groups,
  id,
  isDraggable,
  setNodeRef,
  style,
  attributes,
  listeners,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('groups');
  const { permissions } = useUserPermissions();
  const { shareGroup } = useShareRedirect();

  const userGroup = useMemo(() => groups?.find((group) => group.id === id), [groups, id]);

  if (!userGroup) return null;

  const canShare = permissions[UserPermissionType.GroupShare] && userGroup.group.isPublicGroup;

  const linkToEditFrom = userGroup.group.isDefaultGroup
    ? ROUTES.FavouriteGroupEdit(userGroup?.id)
    : ROUTES.GroupEdit(userGroup?.id);

  const canEdit =
    (userGroup.group.isPublicGroup && permissions[UserPermissionType.PublicGroupEdit]) ||
    (!userGroup.group.isPublicGroup && permissions[UserPermissionType.GroupEdit]);

  return (
    <div className="group-item" style={style} ref={setNodeRef}>
      {isDraggable ? (
        <>
          <div className="group-item__head">
            <span>{userGroup.group.name}</span>
          </div>
        </>
      ) : (
        <div className="group-item__head">
          <span className="group-item__title">{userGroup.group.name}</span>
        </div>
      )}
      <div className="group-item__body">
        {isDraggable ? (
          <div className="group-item__draggable">
            <div className="group-item__draggable-handle" {...attributes} {...listeners}>
              <IconSortHandle />
            </div>
          </div>
        ) : (
          <>
            {userGroup.group.isDefaultGroup ? (
              <PopUpNav
                className="popup-nav--reverse"
                links={[
                  {
                    label: t('editGroup'),
                    onClick: () => navigate(linkToEditFrom),
                    hasLineBelow: false,
                    icon: <IconEdit />,
                  },
                ]}
              />
            ) : (
              <PopUpNav
                className="popup-nav--reverse"
                links={[
                  {
                    label: canEdit ? t('editGroup') : '',
                    onClick: () => navigate(linkToEditFrom),
                    hasLineBelow: canShare,
                    icon: <IconEdit />,
                  },
                  ...(canShare
                    ? [
                        {
                          label: t('shareGroup'),
                          onClick: () => shareGroup(userGroup),
                          icon: <IconShare />,
                        },
                      ]
                    : []),
                ]}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};
